import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Clock, User, AlertCircle, Send } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import axios from 'axios';

export default function TicketDetailUser() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    fetchTicketDetail();
  }, [ticketId]);

  const fetchTicketDetail = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(`${backendUrl}/api/tickets/${ticketId}`);
      setTicket(res.data.ticket);
      setReplies(res.data.replies || []);
    } catch (error) {
      console.error('Erreur chargement ticket:', error);
      if (error.response?.status === 404) {
        toast.error('Ticket non trouvé');
        navigate('/support/tickets');
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || replyMessage.trim().length < 10) {
      toast.error('Votre message doit contenir au moins 10 caractères');
      return;
    }

    setSendingReply(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${backendUrl}/api/tickets/${ticketId}/reply`, {
        message: replyMessage
      });
      
      toast.success('Réponse envoyée avec succès');
      setReplyMessage('');
      fetchTicketDetail(); // Refresh to see new reply and updated status
    } catch (error) {
      console.error('Erreur envoi réponse:', error);
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          const errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          toast.error(`Erreur : ${errorMsg}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error('Erreur lors de l\'envoi');
        }
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } finally {
      setSendingReply(false);
    }
  };

  const handleReopenTicket = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${backendUrl}/api/tickets/${ticketId}/reopen`);
      
      toast.success('Ticket réouvert avec succès. Un agent humain va traiter votre demande.');
      fetchTicketDetail(); // Refresh to see updated status
    } catch (error) {
      console.error('Erreur réouverture ticket:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de la réouverture du ticket');
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { label: 'Ouvert', variant: 'secondary' },
      in_progress: { label: 'En cours', variant: 'default' },
      awaiting_user: { label: 'En attente de votre réponse', variant: 'destructive' },
      resolved: { label: 'Résolu', variant: 'outline' },
      closed: { label: 'Fermé', variant: 'outline' },
      auto_closed: { label: 'Fermé automatiquement', variant: 'secondary' }
    };
    const config = statusConfig[status] || { label: status, variant: 'secondary' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { label: 'Basse', variant: 'secondary' },
      medium: { label: 'Moyenne', variant: 'default' },
      high: { label: 'Haute', variant: 'destructive' },
      urgent: { label: 'Urgente', variant: 'destructive' }
    };
    const config = priorityConfig[priority] || { label: priority, variant: 'secondary' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!ticket) {
    return null;
  }

  const canReply = ticket.status === 'awaiting_user';
  const needsUrgentReply = ticket.status === 'awaiting_user';
  const isInProgress = ticket.status === 'in_progress' || ticket.status === 'open';
  const isClosed = ['resolved', 'closed', 'auto_closed'].includes(ticket.status);

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="ticket-detail-user-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/support')}
            data-testid="back-to-tickets-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Mes demandes
          </Button>
          <div>
            <h1 className="text-2xl font-bold" data-testid="ticket-subject">{ticket.subject}</h1>
            <p className="text-sm text-muted-foreground" data-testid="ticket-id">
              Ticket #{ticket.id.substring(0, 8)}
            </p>
          </div>
        </div>
      </div>

      {/* Urgent Reply Alert */}
      {needsUrgentReply && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div>
                <p className="font-semibold text-destructive">Réponse requise sous 48h</p>
                <p className="text-sm text-muted-foreground">
                  Notre équipe support a besoin d'informations supplémentaires. Veuillez répondre ci-dessous avant que votre demande ne soit automatiquement fermée.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ticket Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Statut</CardTitle>
          </CardHeader>
          <CardContent>
            {getStatusBadge(ticket.status)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Priorité</CardTitle>
          </CardHeader>
          <CardContent>
            {getPriorityBadge(ticket.priority)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Créé le</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>{format(new Date(ticket.created_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Original Ticket Content */}
      <Card>
        <CardHeader>
          <CardTitle>Votre demande initiale</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/50 p-4 rounded-lg">
            <p className="whitespace-pre-wrap">{ticket.description}</p>
          </div>
        </CardContent>
      </Card>

      {/* Conversation/Replies */}
      {replies.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Historique des échanges</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {replies.map((reply, index) => (
              <div key={reply.id}>
                {index > 0 && <Separator className="my-4" />}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {reply.user_role === 'user' || reply.user_role === 'parent' || reply.user_role === 'nanny' ? 'Vous' : 'Support Nannyou'}
                    </span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground">
                      {format(new Date(reply.created_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                    </span>
                  </div>
                  <div className={`p-4 rounded-lg ${
                    reply.user_role === 'user' || reply.user_role === 'parent' || reply.user_role === 'nanny'
                      ? 'bg-primary/10 border-l-4 border-primary'
                      : 'bg-muted/50 border-l-4 border-orange-500'
                  }`}>
                    <p className="whitespace-pre-wrap">{reply.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Reply Form - Only if awaiting user response */}
      {canReply && (
        <Card>
          <CardHeader>
            <CardTitle>Votre réponse (requise)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reply-message">Message</Label>
              <Textarea
                id="reply-message"
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Écrivez votre message (minimum 10 caractères)..."
                rows={6}
                maxLength={2000}
                minLength={10}
                data-testid="reply-message-textarea"
              />
              <p className="text-xs text-muted-foreground">
                {replyMessage.length}/2000 caractères (minimum 10)
              </p>
            </div>
            <Button
              onClick={handleSendReply}
              disabled={sendingReply || replyMessage.trim().length < 10}
              className="w-full"
              data-testid="send-reply-button"
            >
              <Send className="h-4 w-4 mr-2" />
              Envoyer la réponse
            </Button>
          </CardContent>
        </Card>
      )}

      {/* In Progress Message - Waiting for support */}
      {isInProgress && (
        <Card>
          <CardContent className="pt-6 text-center py-8">
            <div className="flex flex-col items-center gap-3">
              <Clock className="h-12 w-12 text-primary" />
              <div>
                <p className="text-lg font-medium mb-2">Demande en cours de traitement</p>
                <p className="text-muted-foreground">
                  Notre équipe support est en train de traiter votre demande. Vous recevrez un email si des informations supplémentaires sont nécessaires.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Closed Ticket Message */}
      {isClosed && (
        <Card>
          <CardContent className="pt-6 text-center py-8">
            {ticket.auto_responded && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-purple-900 mb-1">
                  ✨ Réponse automatique par IA
                </p>
                <p className="text-xs text-purple-700">
                  Cette réponse a été générée automatiquement. Si elle ne répond pas à votre besoin, vous pouvez rouvrir le ticket.
                </p>
              </div>
            )}
            
            <p className="text-muted-foreground mb-2">
              Ce ticket est {ticket.status === 'resolved' ? 'résolu' : 'fermé'}.
            </p>
            
            {ticket.resolution_notes && (
              <div className="bg-green-50 p-4 rounded-lg mt-4 text-left">
                <p className="text-sm font-semibold text-green-900 mb-2">✓ Résolution :</p>
                <p className="text-sm text-green-800 whitespace-pre-wrap">{ticket.resolution_notes}</p>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              <Button
                onClick={handleReopenTicket}
                variant="outline"
                className="gap-2"
                data-testid="reopen-ticket-button"
              >
                ↩️ Rouvrir ce ticket
              </Button>
              <Button
                onClick={() => navigate('/support')}
                data-testid="new-ticket-button"
              >
                Créer une nouvelle demande
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground mt-4">
              Vous pouvez rouvrir ce ticket si le problème persiste ou si vous avez besoin d'aide supplémentaire.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
